import {
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ListRemittancesInput,
  ListRemittancesResponse,
  RemittanceListItem,
  RemittanceDetail,
  CreateRemittanceInput,
  UpdateRemittanceInput,
  AddRemittanceLineItemsInput,
  UpdateRemittanceLineItemInput,
  DeleteRemittanceLineItemInput,
} from '@erp/rpc';
// AR Technical Demo (F0 / GEN-2256) — demo-mode shim. Delete with frontend/src/demo/ar.
import {
  arDemoActive,
  useArDemoFixture,
  withArDemoData,
} from '@/demo/ar/hook-shim';

export function useArRemittancesList(filters: ListRemittancesInput = {}) {
  const demoData = useArDemoFixture(
    (s): ListRemittancesResponse => ({
      remittances: s.remittanceList,
      pagination: {
        total: s.remittanceList.length,
        limit: 100,
        offset: 0,
      },
    }),
  );
  const baseQuery = useQuery({
    ...orpc.accountsReceivable.arRemittances.list.queryOptions({
      input: filters,
    }),
    enabled: !arDemoActive(),
  });
  const query = withArDemoData(baseQuery, demoData);

  return {
    ...query,
    remittances: (query.data?.remittances ?? []) as RemittanceListItem[],
    pagination: query.data?.pagination,
  } satisfies typeof query & {
    data?: ListRemittancesResponse;
    remittances: RemittanceListItem[];
    pagination: ListRemittancesResponse['pagination'] | undefined;
  };
}

export function useArRemittance(id: string | undefined, enabled = true) {
  const demoData = useArDemoFixture((s) =>
    id ? { remittance: s.remittancesById[id] ?? null } : undefined,
  );
  const baseQuery = useQuery({
    ...orpc.accountsReceivable.arRemittances.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled && !arDemoActive(),
  });
  const query = withArDemoData(baseQuery, demoData);

  return {
    ...query,
    remittance: (query.data?.remittance ?? null) as RemittanceDetail | null,
  };
}

function useInvalidateRemittances() {
  const queryClient = useQueryClient();
  return async (remittanceAdviceId?: string) => {
    // `.key()` (no args) returns the procedure-level prefix so every cached
    // list variant (filters, sort, pagination) gets invalidated. Using
    // `.queryOptions({ input: {} }).queryKey` only matched empty-input lists,
    // so any filtered/paged list view stayed stale after a mutation.
    await queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.arRemittances.list.key(),
    });

    if (remittanceAdviceId) {
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arRemittances.get.key({
          input: { id: remittanceAdviceId },
        }),
      });
    }
  };
}

export function useConfirmLineItemMatch() {
  const invalidate = useInvalidateRemittances();

  return useMutation({
    mutationFn: async (input: { lineItemId: string; invoiceId: string }) => {
      return rpcClient.accountsReceivable.arRemittances.confirmMatch(input);
    },
    onSuccess: (data) => invalidate(data.lineItem.remittanceAdviceId),
  });
}

export function useRejectLineItem() {
  const invalidate = useInvalidateRemittances();

  return useMutation({
    mutationFn: async (input: { lineItemId: string }) => {
      return rpcClient.accountsReceivable.arRemittances.rejectLineItem(input);
    },
    onSuccess: (data) => invalidate(data.lineItem.remittanceAdviceId),
  });
}

export function useUndoRejectLineItem() {
  const invalidate = useInvalidateRemittances();

  return useMutation({
    mutationFn: async (input: { lineItemId: string }) => {
      return rpcClient.accountsReceivable.arRemittances.undoRejectLineItem(
        input,
      );
    },
    onSuccess: (data) => invalidate(data.lineItem.remittanceAdviceId),
  });
}

export function useLinkEmail() {
  const invalidate = useInvalidateRemittances();

  return useMutation({
    mutationFn: async (input: {
      remittanceAdviceId: string;
      inboundEmailId: string;
    }) => {
      return rpcClient.accountsReceivable.arRemittances.linkEmail(input);
    },
    onSuccess: (_data, variables) => invalidate(variables.remittanceAdviceId),
  });
}

export function useOverrideRemittanceCustomer() {
  const invalidate = useInvalidateRemittances();

  return useMutation({
    mutationFn: async (input: {
      remittanceAdviceId: string;
      customerId: string;
    }) => {
      return rpcClient.accountsReceivable.arRemittances.overrideCustomer(input);
    },
    onSuccess: (data) => invalidate(data.remittance.id),
  });
}

export function useCreateRemittance() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (input: CreateRemittanceInput) => {
      return rpcClient.accountsReceivable.arRemittances.create(input);
    },
    onSuccess: (data) => invalidate(data.remittance.id),
  });
}

export function useUpdateRemittance() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (input: UpdateRemittanceInput) => {
      return rpcClient.accountsReceivable.arRemittances.update(input);
    },
    onSuccess: (data) => invalidate(data.remittance.id),
  });
}

export function useAddRemittanceLineItems() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (input: AddRemittanceLineItemsInput) => {
      return rpcClient.accountsReceivable.arRemittances.addLineItems(input);
    },
    onSuccess: (data) => invalidate(data.remittance.id),
  });
}

export function useUpdateRemittanceLineItem() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (input: UpdateRemittanceLineItemInput) => {
      return rpcClient.accountsReceivable.arRemittances.updateLineItem(input);
    },
    onSuccess: (data) => invalidate(data.remittance.id),
  });
}

export function useDeleteRemittanceLineItem() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (input: DeleteRemittanceLineItemInput) => {
      return rpcClient.accountsReceivable.arRemittances.deleteLineItem(input);
    },
    onSuccess: (data) => invalidate(data.remittance.id),
  });
}

export function useDeleteRemittance() {
  const invalidate = useInvalidateRemittances();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string }) => {
      console.log('[useDeleteRemittance] mutationFn entry', input);
      const deleteFn = rpcClient.accountsReceivable.arRemittances.delete;
      console.log('[useDeleteRemittance] rpcClient.delete is', typeof deleteFn);
      const result =
        await rpcClient.accountsReceivable.arRemittances.delete(input);
      console.log('[useDeleteRemittance] mutationFn result', result);
      return result;
    },
    onSuccess: (_data, variables) => {
      console.log('[useDeleteRemittance] onSuccess', variables);
      // Delete also hard-deletes the linked ar_payment, so drop cached AR
      // payment lists (all filtered variants) alongside remittance queries.
      return Promise.all([
        invalidate(variables.id),
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arPayments.list.key(),
        }),
      ]);
    },
    onError: (error, variables) => {
      console.error('[useDeleteRemittance] onError', { variables, error });
    },
  });
}
